type Obj = Record<string, any>;

const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];

const isPlainObject = (value: unknown): value is Obj =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// Whitespace/control chars are ignored by browsers inside the scheme ("java\tscript:")
const DANGEROUS_URL = /^(javascript|data|vbscript):/i;

/** Highcharts navigates to some strings on click (credits.href), so script-like URLs are blanked anywhere in the tree. */
export function neutralizeUrls<T>(value: T): T {
    if (typeof value === 'string') {
        // eslint-disable-next-line no-control-regex
        return (DANGEROUS_URL.test(value.replace(/[\s\u0000-\u001f]/g, '')) ? '' : value) as T;
    }
    if (Array.isArray(value)) {
        return value.map(neutralizeUrls) as T;
    }
    if (isPlainObject(value)) {
        const result: Obj = {};
        for (const key of Object.keys(value)) {
            result[key] = neutralizeUrls(value[key]);
        }
        return result as T;
    }
    return value;
}

function mergeObjects(base: Obj, override: Obj): Obj {
    const result: Obj = {...base};
    for (const key of Object.keys(override)) {
        if (FORBIDDEN_KEYS.includes(key) || override[key] === undefined) {
            continue;
        }
        // Doctrine turns an empty nested `{}` into `[]`, which must not wipe out a default object
        const value = isPlainObject(result[key]) && Array.isArray(override[key]) && override[key].length === 0
            ? {}
            : override[key];
        result[key] = isPlainObject(result[key]) && isPlainObject(value)
            ? mergeObjects(result[key], value)
            : value;
    }
    return result;
}

function mergeSeries(base: Obj[], override: Obj[]): Obj[] {
    const result = [...base];
    for (const item of override) {
        const index = item?.id === undefined ? -1 : result.findIndex(existing => existing.id === item.id);
        if (index >= 0) {
            result[index] = mergeObjects(result[index], item);
        } else {
            result.push(item);
        }
    }
    return result;
}

/** Lays trusted renderer output over defaults; unlike the user override it is not URL-neutralized (labels may legitimately start with "data:"). */
export function applyDefaults<T extends Obj>(defaults: Obj, options: T): T {
    return mergeObjects(defaults, options) as T;
}

/**
 * Applies spec.highcharts over rendered options: objects merge recursively, arrays replace (never index-wise),
 * except `series`, which is matched by `id` and unmatched items are appended.
 */
export default function mergeHighcharts<T extends Obj>(base: T, override: Obj | undefined): T {
    if (!isPlainObject(override)) {
        return base;
    }
    const {series, ...rest} = neutralizeUrls(override);
    const merged = mergeObjects(base, rest);
    if (Array.isArray(series)) {
        merged.series = mergeSeries(Array.isArray(base.series) ? base.series : [], series);
    }
    return merged as T;
}
